import type { DefLookup } from "../catalog";
import { pvInputTerminalGroups } from "../component-ports";
import type { Issue, System } from "../types";
import { buildGraph, computeConnectedSolarInputs, splitV, v } from "./graph";

/** Judge physical wiring with switches closed, independently of sun state or
 * battery-side charging availability. A common negative alone does not imply
 * that an optional PV input is intended to be used. */
export function detectIncompletePvInput(system: System, lookup: DefLookup): Issue[] {
  const complete = computeConnectedSolarInputs(system, lookup);
  const graph = buildGraph(system, lookup, { excludeSense: true });
  const definitions = new Map(system.nodes.filter(n => n.kind === "component").map(n => [n.id, lookup(n.definitionId, n.customDefinition)]));
  // A return-only array lead is evidence of intended PV wiring, even on a
  // common-negative controller. A general battery return is not: it cannot
  // identify which optional PV input an array elsewhere belongs to.
  const leadsToArray = (start: string, controller: string): boolean => {
    const seen = new Set<string>([start]), queue = [start];
    let panel = false;
    for (let i = 0; i < queue.length; i++) {
      const id = splitV(queue[i]).nodeId;
      if (id === controller) continue;
      const def = definitions.get(id);
      if (def?.category === "battery") return false;
      if (def?.category === "solar") panel = true;
      if (def?.category === "charger") continue;
      for (const { vertex } of graph.adj.get(queue[i]) ?? []) if (!seen.has(vertex)) { seen.add(vertex); queue.push(vertex); }
    }
    return panel;
  };
  const issues: Issue[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (def?.category !== "charger") continue;
    for (const port of pvInputTerminalGroups(def)) {
      const positives = port.filter(t => t.polarity === "positive");
      const negatives = port.filter(t => t.polarity === "negative");
      // Ambiguous imported definitions are not proof of a missing wire.
      if (positives.length === 0 || negatives.length === 0) continue;
      const positiveIds = new Set(positives.map(t => t.id)), negativeIds = new Set(negatives.map(t => t.id));
      const attached = system.cables.filter(c => {
        const from = c.fromNodeId === node.id, to = c.toNodeId === node.id;
        if ((!from || !positiveIds.has(c.fromTerminalId)) && (!to || !positiveIds.has(c.toTerminalId))) {
          if (from && negativeIds.has(c.fromTerminalId)) return leadsToArray(v(c.toNodeId, c.toTerminalId), node.id);
          if (to && negativeIds.has(c.toTerminalId)) return leadsToArray(v(c.fromNodeId, c.fromTerminalId), node.id);
          return false;
        }
        return true;
      });
      if (attached.length === 0) continue;
      if (complete.some(path => path.controllerNodeId === node.id &&
        positives.every(t => path.positiveTerminals.has(v(node.id, t.id))) &&
        negatives.every(t => path.negativeTerminals.has(v(node.id, t.id))))) continue;
      const label = node.label ?? def.shortName ?? def.name;
      issues.push({
        code: "pv-input-incomplete",
        severity: "warning",
        title: { key: "diag.pv-input-incomplete.title", vars: { label } },
        message: { key: "diag.pv-input-incomplete.message", vars: { label } },
        fix: { key: "diag.pv-input-incomplete.fix" },
        nodeIds: [node.id],
        cableIds: attached.map(c => c.id),
      });
    }
  }
  return issues;
}
